import type { FC, PropsWithChildren, ReactElement } from 'react';
import { createContext, useContext, useEffect } from 'react';
import isFunction from 'lodash/isFunction';

import type { PropsWithRequiredChildren } from 'Types/react/react';

type QueryViewStateHandlerProps = {
	isEmpty?: boolean;
	isError?: boolean;
	isLoading?: boolean;
	isSuccess?: boolean;
};

export const enum QueryViewState {
	EMPTY = 'empty',
	ERROR = 'error',
	LOADING = 'loading',
	SUCCESS = 'success',
}

type EmptyViewProps = {
	handleOnEmpty?: () => void;
};

export const QueryViewStateHandlerContext = createContext<QueryViewState | null>(null);

const useQueryViewStateHandlerContext = () => {
	const context = useContext(QueryViewStateHandlerContext);
	if (!context) {
		throw new Error(
			'QueryViewStateHandler compound components cannot be rendered outside the QueryViewStateHandler component',
		);
	}
	return context;
};

export const LoadingView: FC<PropsWithRequiredChildren> = ({ children }) => {
	const view = useQueryViewStateHandlerContext();
	return view === QueryViewState.LOADING ? <>{children}</> : null;
};

export const ErrorView: FC<PropsWithRequiredChildren> = ({ children }) => {
	const view = useQueryViewStateHandlerContext();
	return view === QueryViewState.ERROR ? <>{children}</> : null;
};

export const EmptyView: FC<PropsWithRequiredChildren<EmptyViewProps>> = ({ children, handleOnEmpty }) => {
	const view = useQueryViewStateHandlerContext();
	const isEmpty = view === QueryViewState.EMPTY;

	useEffect(() => {
		if (isEmpty && isFunction(handleOnEmpty)) {
			handleOnEmpty();
		}
	}, [isEmpty, handleOnEmpty]);

	return isEmpty ? <>{children}</> : null;
};

export const SuccessView: FC<PropsWithRequiredChildren> = ({ children }) => {
	const view = useQueryViewStateHandlerContext();
	return view === QueryViewState.SUCCESS ? <>{children}</> : null;
};

export const QueryViewStateHandler = ({
	children,
	isEmpty,
	isError,
	isLoading,
	isSuccess,
}: PropsWithChildren<QueryViewStateHandlerProps>): ReactElement => {
	let state = null;

	if (isError) {
		state = QueryViewState.ERROR;
	} else if (isLoading) {
		state = QueryViewState.LOADING;
	} else if (isEmpty) {
		state = QueryViewState.EMPTY;
	} else if (isSuccess) {
		state = QueryViewState.SUCCESS;
	}

	return <QueryViewStateHandlerContext.Provider value={state}>{children}</QueryViewStateHandlerContext.Provider>;
};
